import { config } from '_appConfig'

import { updateSession, saveScormEvents } from '../api'
import { ContentSpaceSessionStatus, IContentSpaceSession, ScormEventType } from '../types'

interface IScormEventLog {
  eventType: ScormEventType
  element?: string
  value?: string
  oldValue?: string
  errorCode?: string
  timestamp: string
}

interface IScorm12API {
  LMSInitialize(param: string): string
  LMSFinish(param: string): string
  LMSGetValue(element: string): string
  LMSSetValue(element: string, value: string): string
  LMSCommit(param: string): string
  LMSGetLastError(): string
  LMSGetErrorString(errorCode: string): string
  LMSGetDiagnostic(errorCode: string): string
}

interface IScorm2004API {
  Initialize(param: string): string
  Terminate(param: string): string
  GetValue(element: string): string
  SetValue(element: string, value: string): string
  Commit(param: string): string
  GetLastError(): string
  GetErrorString(errorCode: string): string
  GetDiagnostic(errorCode: string): string
}

export interface IScormSessionSnapshot {
  session: IContentSpaceSession
  terminated: boolean
}

type Listener = () => void

// Императивный владелец SCORM-lifecycle. Живёт один инстанс на одну сессию
// (см. ScormSessionProvider). Источник правды для `session` и `cmiData`:
// - `cmiData` — аккумулятор CMI, мутабельный (SCORM-бандл пишет сотни раз —
//   незачем плодить immutable-копии и будить React на каждом Set).
// - `snapshot` — то, что видит React через useSyncExternalStore; меняется только
//   после успешного `commit()` к бэку. Ссылка обновляется, подписчики ререндерятся.
export class ScormSession {
  private cmiData: Record<string, string>
  private snapshot: IScormSessionSnapshot
  private listeners: Set<Listener> = new Set()
  private eventBuffer: IScormEventLog[] = []
  private flushTimeout: ReturnType<typeof setTimeout> | null = null
  private unloadWindow: Window | null = null
  private unloadHandler: (() => void) | null = null
  private disposed = false

  constructor(initialSession: IContentSpaceSession) {
    const existing = (initialSession.cmiData as Record<string, string> | null | undefined) ?? {}
    this.cmiData = { ...existing }

    const studentId = initialSession.email || `session-${initialSession.id}@contentspace.parta.io`
    this.cmiData['cmi.core.student_id'] = studentId
    this.cmiData['cmi.core.student_name'] =
      initialSession.userName || initialSession.email || 'Anonymous'
    this.cmiData['cmi.core.lesson_status'] = mapStatusToScorm(initialSession.status)
    this.cmiData['cmi.core.score.raw'] = initialSession.score?.toString() || ''
    this.cmiData['cmi.core.lesson_location'] = initialSession.location || ''
    // В `session.cmiData` суспенд лежит ключом внутри объекта (бэк хранит целиком
    // наш `cmiData`). Брать вложенное значение, а не stringify объекта — иначе
    // SCORM-бандл получит мусор и не восстановит позицию секций.
    const suspendData = existing['cmi.suspend_data'] || ''
    this.cmiData['cmi.suspend_data'] = suspendData
    this.cmiData['cmi.learner_id'] = studentId
    this.cmiData['cmi.learner_name'] =
      initialSession.userName || initialSession.email || 'Anonymous'

    // SCORM-spec: LMS обязан выставить `cmi.(core.)?entry` перед init'ом:
    //   'resume'    — возобновляем сессию с накопленным `cmi.suspend_data`
    //   'ab-initio' — первый вход
    // Без этого бандл читает '' через initRead, считает ab-initio и стартует
    // с первой секции, полностью игнорируя наш сохранённый suspend_data.
    // Симптом — @CS-E2E-POR-003-ASK: после Continue Session SCORM открывает
    // первую секцию вместо второй, хотя в DB `cmi.suspend_data` есть (948B).
    // Флаг строим по факту наличия прежнего suspend_data, не по полю entry —
    // бандл мог оставить его '' между коммитами.
    const entry = suspendData ? 'resume' : 'ab-initio'
    this.cmiData['cmi.core.entry'] = entry
    this.cmiData['cmi.entry'] = entry

    this.snapshot = { session: initialSession, terminated: false }
  }

  // useSyncExternalStore API.
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = (): IScormSessionSnapshot => this.snapshot

  public acknowledgeTerminate = () => {
    if (!this.snapshot.terminated) {
      return
    }
    this.snapshot = { ...this.snapshot, terminated: false }
    this.notify()
  }

  public init(win: Window) {
    if (this.disposed) {
      return
    }

    const scorm12Api: IScorm12API = {
      LMSInitialize: () => {
        this.logEvent('LMSInitialize')
        return 'true'
      },
      LMSFinish: () => {
        this.logEvent('LMSFinish')
        this.commit().then(() => this.markTerminated())
        return 'true'
      },
      LMSGetValue: (element) => {
        const value = this.cmiData[element] || ''
        this.logEvent('LMSGetValue', element, value)
        return value
      },
      LMSSetValue: (element, value) => {
        const oldValue = this.cmiData[element]
        this.cmiData[element] = value
        this.logEvent('LMSSetValue', element, value, oldValue)
        return 'true'
      },
      LMSCommit: () => {
        this.logEvent('LMSCommit')
        this.commit()
        return 'true'
      },
      LMSGetLastError: () => '0',
      LMSGetErrorString: () => 'No error',
      LMSGetDiagnostic: () => 'No error',
    }

    const scorm2004Api: IScorm2004API = {
      Initialize: () => {
        this.logEvent('Initialize')
        return 'true'
      },
      Terminate: () => {
        this.logEvent('Terminate')
        this.commit().then(() => this.markTerminated())
        return 'true'
      },
      GetValue: (element) => {
        const value = this.cmiData[element] || ''
        this.logEvent('GetValue', element, value)
        return value
      },
      SetValue: (element, value) => {
        const oldValue = this.cmiData[element]
        this.cmiData[element] = value
        this.logEvent('SetValue', element, value, oldValue)
        return 'true'
      },
      Commit: () => {
        this.logEvent('Commit')
        this.commit()
        return 'true'
      },
      GetLastError: () => '0',
      GetErrorString: () => 'No error',
      GetDiagnostic: () => 'No error',
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(win as any).API = scorm12Api
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(win as any).API_1484_11 = scorm2004Api

    // Синхронный flush на закрытии/рефреше: обычный fetch не успевает улететь,
    // cmi.suspend_data теряется, следующий open начинает с первой секции.
    // `sendBeacon` гарантированно уходит на unload.
    this.unloadHandler = () => this.commitBeacon()
    this.unloadWindow = win
    win.addEventListener('pagehide', this.unloadHandler)
  }

  public dispose() {
    this.disposed = true
    if (this.unloadWindow && this.unloadHandler) {
      this.unloadWindow.removeEventListener('pagehide', this.unloadHandler)
    }
    this.unloadWindow = null
    this.unloadHandler = null
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
      this.flushTimeout = null
    }
    this.listeners.clear()
  }

  private notify() {
    this.listeners.forEach((l) => l())
  }

  private markTerminated() {
    this.snapshot = { ...this.snapshot, terminated: true }
    this.notify()
  }

  private logEvent(
    eventType: ScormEventType,
    element?: string,
    value?: string,
    oldValue?: string,
    errorCode?: string,
  ) {
    this.eventBuffer.push({
      eventType,
      element,
      value,
      oldValue,
      errorCode,
      timestamp: new Date().toISOString(),
    })
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
    }
    this.flushTimeout = setTimeout(() => this.flushEvents(), 5000)
  }

  private async flushEvents() {
    if (this.eventBuffer.length === 0) {
      return
    }
    const events = [...this.eventBuffer]
    this.eventBuffer = []
    const { sessionToken, id } = this.snapshot.session
    if (!sessionToken) {
      return
    }
    try {
      await saveScormEvents(id, sessionToken, events)
    } catch (e) {
      console.error('Failed to save SCORM events', e)
      this.eventBuffer = [...events, ...this.eventBuffer]
    }
  }

  private buildSessionPayload() {
    const data = this.cmiData
    let status = data['cmi.core.lesson_status']
    const completionStatus = data['cmi.completion_status']
    const successStatus = data['cmi.success_status']

    if (completionStatus === 'completed') {
      if (successStatus === 'passed') {
        status = 'passed'
      } else if (successStatus === 'failed') {
        status = 'failed'
      } else {
        status = 'completed'
      }
    } else if (successStatus === 'passed') {
      status = 'passed'
    } else if (successStatus === 'failed') {
      status = 'failed'
    }

    const score = data['cmi.core.score.raw'] || data['cmi.score.raw']
    const location = data['cmi.core.lesson_location'] || data['cmi.location']

    // BUG-4143: SCORM score range is sent to BE so UI can normalize display to 0..100
    const scoreMax = Number.parseFloat(data['cmi.core.score.max'] || data['cmi.score.max'] || '100')
    const scoreMin = Number.parseFloat(data['cmi.core.score.min'] || data['cmi.score.min'] || '0')
    const range = scoreMax - scoreMin

    let progress: number | undefined
    const progressMeasure = data['cmi.progress_measure']
    if (progressMeasure) {
      const parsedMeasure = Number.parseFloat(progressMeasure)
      if (!Number.isNaN(parsedMeasure)) {
        progress = Math.round(parsedMeasure * 100)
      }
    } else if (status === 'completed' || status === 'passed' || status === 'failed') {
      progress = 100
    } else if (score) {
      const scoreValue = Number.parseFloat(score)
      if (!Number.isNaN(scoreValue) && !Number.isNaN(range) && range > 0) {
        progress = Math.max(0, Math.min(100, Math.round(((scoreValue - scoreMin) / range) * 100)))
      }
    }

    const newStatus = mapScormToStatus(status || 'incomplete')
    const parsedScore = score ? Number.parseFloat(score) : NaN
    const newScore = Number.isNaN(parsedScore) ? undefined : parsedScore

    return {
      newStatus,
      newScore,
      progress,
      location,
      payload: {
        sessionToken: this.snapshot.session.sessionToken || undefined,
        status: newStatus,
        score: newScore,
        scoreMin: Number.isNaN(scoreMin) ? undefined : scoreMin,
        scoreMax: Number.isNaN(scoreMax) ? undefined : scoreMax,
        progress,
        location,
        cmiData: data,
      },
    }
  }

  private async commit() {
    if (this.disposed) {
      return
    }
    const { newStatus, newScore, progress, location, payload } = this.buildSessionPayload()
    try {
      await Promise.all([updateSession(this.snapshot.session.id, payload), this.flushEvents()])
      if (this.disposed) {
        return
      }
      this.snapshot = {
        ...this.snapshot,
        session: {
          ...this.snapshot.session,
          status: newStatus,
          score: newScore ?? null,
          // progress undefined — SCORM не посчитал, не трогаем поле
          ...(progress !== undefined && { progress }),
          location: location ?? null,
        },
      }
      this.notify()
    } catch (e) {
      console.error('Failed to save session progress', e)
    }
  }

  private commitBeacon() {
    const { sessionToken, id } = this.snapshot.session
    if (!sessionToken) {
      return
    }
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return
    }
    const { payload } = this.buildSessionPayload()
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon(`${config.io.contentspaceEndpoint}/public/sessions/${id}/beacon`, blob)
  }
}

function mapStatusToScorm(status: ContentSpaceSessionStatus): string {
  switch (status) {
    case 'COMPLETED':
      return 'completed'
    case 'PASSED':
      return 'passed'
    case 'FAILED':
      return 'failed'
    case 'IN_PROGRESS':
      return 'incomplete'
    default:
      return 'not attempted'
  }
}

function mapScormToStatus(status: string): ContentSpaceSessionStatus {
  switch (status) {
    case 'completed':
      return 'COMPLETED'
    case 'passed':
      return 'PASSED'
    case 'failed':
      return 'FAILED'
    case 'incomplete':
      return 'IN_PROGRESS'
    default:
      return 'IN_PROGRESS'
  }
}
